import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { formatBRL } from "@/lib/constants";
import { Lock, CheckCircle2, Loader2, CreditCard, QrCode, LogOut, ShieldCheck } from "lucide-react";

const ORDER = ["basic", "professional", "enterprise", "anual_pro"];
const FEATS = {
  basic: ["100 documentos", "300 clientes", "Todos os tipos de documento", "Fotos e assinatura digital", "Compartilhar no WhatsApp"],
  professional: ["1.000 documentos", "3.000 clientes", "Laudos técnicos", "Até 3 usuários", "Cobrança automática (WhatsApp/E-mail)", "Recibos com QR Code", "Relatórios financeiros (PDF/CSV)"],
  enterprise: ["Documentos ilimitados", "Clientes ilimitados", "Até 20 usuários", "Recursos avançados", "Suporte prioritário"],
  anual_pro: ["Tudo ilimitado", "Todos os tipos de documento", "Laudos técnicos", "Até 5 usuários", "Relatórios e recursos avançados", "12 meses de acesso"],
};

export default function Paywall({ onActivated }) {
  const { logout } = useAuth();
  const [plans, setPlans] = useState(null);
  const [billing, setBilling] = useState("monthly");
  const [busy, setBusy] = useState("");
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    api.get("/plans").then(({ data }) => setPlans(data)).catch(() => setPlans({}));
    (async () => {
      try {
        const { data } = await api.post("/subscription/confirm");
        if (data.activated) { toast.success("Pagamento confirmado! Bem-vindo de volta 🎉"); onActivated?.(); return; }
      } catch (e) { console.error("subscription confirm poll failed", e); }
      setChecking(false);
    })();
  }, []);

  const subscribe = async (plan, billingOverride) => {
    setBusy(plan);
    try {
      const b = billingOverride || billing;
      const { data } = await api.post("/subscription/checkout", { plan, billing: b, back_url: window.location.origin + "/dashboard" });
      if (data.init_point) window.location.assign(data.init_point);
      else toast.error("Não foi possível iniciar o pagamento");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao iniciar pagamento"); } finally { setBusy(""); }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="paywall-screen">
      <div className="max-w-5xl mx-auto px-4 py-10 sm:py-14">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-2.5">
            <img src="/np-logo.png" alt="NotaHoje" className="h-9 w-9 rounded-lg bg-white object-contain p-0.5 border border-border" />
            <span className="font-display font-extrabold text-lg">NotaHoje</span>
          </div>
          <Button variant="ghost" size="sm" onClick={logout} data-testid="paywall-logout-btn"><LogOut className="h-4 w-4 mr-1" /> Sair</Button>
        </div>

        <div className="text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center justify-center h-16 w-16 rounded-2xl bg-primary/10 text-primary mb-5">
            <Lock className="h-8 w-8" />
          </div>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight">Seu período de teste terminou</h1>
          <p className="text-muted-foreground mt-3 text-base">
            Seus dados estão salvos e seguros. Escolha um plano para continuar criando documentos e enviando aos seus clientes.
          </p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-3 text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-1.5"><QrCode className="h-4 w-4 text-primary" /> Pix</span>
            <span className="inline-flex items-center gap-1.5"><CreditCard className="h-4 w-4 text-primary" /> Cartão de crédito</span>
            <span className="inline-flex items-center gap-1.5"><CreditCard className="h-4 w-4 text-primary" /> Cartão de débito</span>
          </div>
        </div>

        <div className="mt-8">
          <div className="flex justify-center">
            <div className="inline-flex rounded-xl border border-border bg-card p-1" data-testid="paywall-billing-toggle">
              <button onClick={() => setBilling("monthly")} className={`px-5 py-2 rounded-lg text-sm font-semibold transition-colors ${billing === "monthly" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`} data-testid="paywall-billing-monthly">Mensal</button>
              <button onClick={() => setBilling("annual")} className={`px-5 py-2 rounded-lg text-sm font-semibold transition-colors ${billing === "annual" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`} data-testid="paywall-billing-annual">Anual <span className="ml-1 text-[10px] bg-secondary/15 text-secondary px-1.5 py-0.5 rounded-full align-middle">-17%</span></button>
            </div>
          </div>
          {billing === "annual" && <p className="text-center text-xs text-secondary font-semibold mt-2">Pagando no plano anual você economiza cerca de 2 meses.</p>}
        </div>

        {plans?.anual_pro && plans?.professional && (plans.professional.price * 12 - plans.anual_pro.price_annual) > 0 && (
          <div className="mt-5 mx-auto max-w-2xl rounded-2xl border border-secondary/30 bg-secondary/10 p-4 text-center" data-testid="annual-savings-banner">
            <p className="text-sm font-semibold text-secondary">💡 Anual Pro por {formatBRL(plans.anual_pro.price_annual)}/ano</p>
            <p className="text-xs text-muted-foreground mt-1">No Profissional mensal você pagaria {formatBRL(plans.professional.price * 12)} por ano. Assinando o Anual Pro você economiza <b className="text-secondary">{formatBRL(plans.professional.price * 12 - plans.anual_pro.price_annual)}</b> — quase 2 meses grátis.</p>
          </div>
        )}

        {!plans || checking ? (
          <div className="h-48 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
            {ORDER.map((id) => {
              const p = plans[id];
              if (!p) return null;
              const popular = id === "professional";
              const annualOnly = !!p.annual_only;
              const price = (annualOnly || billing === "annual") ? p.price_annual : p.price;
              const per = (annualOnly || billing === "annual") ? "ano" : "mês";
              const savePct = (!annualOnly && p.price > 0 && p.price_annual > 0) ? Math.round((1 - p.price_annual / (p.price * 12)) * 100) : 0;
              return (
                <Card key={id} className={`p-6 relative flex flex-col ${popular ? "border-primary ring-2 ring-primary/30 shadow-lg" : ""} ${annualOnly ? "border-secondary ring-2 ring-secondary/30" : ""}`} data-testid={`paywall-plan-${id}`}>
                  {popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs font-bold px-3 py-1 rounded-full">MAIS VENDIDO</span>}
                  {annualOnly && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-secondary text-secondary-foreground text-xs font-bold px-3 py-1 rounded-full">MELHOR VALOR</span>}
                  <h3 className="font-display font-bold text-xl">{p.label}</h3>
                  <p className="mt-2"><span className="text-3xl font-display font-extrabold">{formatBRL(price)}</span><span className="text-muted-foreground text-sm">/{per}</span></p>
                  {annualOnly ? <p className="text-xs font-semibold text-secondary mt-1">Plano anual — 12 meses</p> : (billing === "annual" && savePct > 0 && <p className="text-xs font-semibold text-secondary mt-1">Economize {savePct}% no anual</p>)}
                  <ul className="mt-4 space-y-2 flex-1">
                    {FEATS[id].map((f) => <li key={f} className="flex items-start gap-2 text-sm"><CheckCircle2 className="h-4 w-4 text-secondary shrink-0 mt-0.5" />{f}</li>)}
                  </ul>
                  <Button className="w-full mt-6" variant={popular ? "default" : "outline"} disabled={busy === id} onClick={() => subscribe(id, annualOnly ? "annual" : billing)} data-testid={`paywall-subscribe-${id}`}>
                    {busy === id ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Assinar {p.label}</>}
                  </Button>
                </Card>
              );
            })}
          </div>
        )}

        <p className="text-center text-xs text-muted-foreground mt-8 flex items-center justify-center gap-1.5">
          <ShieldCheck className="h-4 w-4" /> Pagamento seguro processado pelo Mercado Pago.
        </p>
      </div>
    </div>
  );
}
